import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import config from '../../config';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import {
  getCurrentAccount,
  getCurrentAccountUid,
} from '../../store/selectors/accountSelectors';
import paymentTypes, {
  PAYMENT_METHOD_TYPES_ICONS,
} from '../../util/enum/api/paymentTypes';
import { ORDER_ENTRY_TYPES } from '../../util/enum/api/orderEntryTypes';
import {
  emptyQuotesOrderStorageBuy,
  executeQuotesForOrderStorageBuy,
  getQuotesForBuyDelivery,
  getQuotesForOrderStorageBuy,
} from '../../store/actions/orders/orderActions';
import { COUNTDOWN_TIMER_SEC } from '../../constants/timerConstants';
import useStartTimer from '../../util/hooks/useStartTimer';
import { handleExpiration } from '../../util/helpers/timerHelpers';
import PriceLockTimer from '../Common/Timers/PriceLockTimer';
import OrderReviewPriceList from './OrderReviewPriceList';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-ico.svg';
import { ReactComponent as HandCoinsIcon } from '../../assets/images/svg/hand-coins.svg';
import Paragraph from '../Paragraph/Paragraph';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import RowItemElement from './RowItem';
import SelectPaymentOptions from './SelectPaymentOptions';
import ModalButtons from '../Modal/ModalButtons';
import PriceTiers from './PriceTiers';
import {
  accountTransactionsFetch,
  fetchPortfolioProductBySymbol,
} from '../../store/actions/portfolio/portfolioActions';
import { PRODUCT_TRANSACTIONS } from '../../constants/accountTransactionConstants';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  IS_FUND_MODAL_OPEN,
  SIGNIFYD_SESSION_ID,
  STANDARD_DEPOSIT_WIZARD,
} from '../../constants/sessionStorage';
import { loadSignifydDeviceFingerprintScriptWithoutSessionId } from '../../signifydDeviceFingerprint';
import { ALL_ZEROES_UID } from '../../constants/mockScenarios';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import StandardDepositWizard from '../Modals/Deposit/StandardDepositWizard/StandardDepositWizard';
import IraDepositWizard from '../Modals/DepositWizard/IraDepositWizard';
import MilleniumDepositWizard from '../Modals/DepositWizard/MilleniumDepositWizard';
import { IRA_ENTRUST_TYPE, IRA_EQUITY_TYPE } from '../../util/constants';
import { deleteWizardKey } from '../../util/helpers/wizardHelpers';
import { useBuyFlowStepNameEvent } from '../../util/helpers/crmEventsHelper';
import {
  BUY_FLOW_ORDER_TYPE,
  BUY_FLOW_STEPS_ENUM,
} from '../../util/enum/api/crmEvents';

const ProductImage = styled.img`
  width: ${pxToRem(150)};
  object-fit: contain;
  margin-bottom: ${pxToRem(10)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 100%;
  }
`;

const ProductInfoWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProductName = styled.h2`
  font-size: ${pxToRem(20)};
  font-weight: 400;
  color: ${themeColors.colorTextPrimary};
`;

const SavingInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.isCCSelected
      ? themeColors.colorBackgroundSecondary
      : themeColors.colorGreenDarkOpacity};
  border: 1px solid
    ${(props) =>
      props.isCCSelected
        ? themeColors.colorPrimaryLight
        : themeColors.colorBadgeSuccess};
  padding: ${pxToRem(4)};
  border-radius: ${variables.borderRadius.borderRadius};
  margin: ${pxToRem(12)} 0;

  ${Paragraph} {
    font-size: ${pxToRem(14)};
    color: ${(props) =>
      props.isCCSelected
        ? themeColors.colorTextSecondary
        : themeColors.colorSuccess};
    font-weight: 400;
    margin-left: ${pxToRem(6)};
  }
`;

const ButtonWrapper = styled.div`
  button {
    height: ${pxToRem(44)};
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    button {
      height: ${pxToRemMd(44)};
    }
  }
`;

const OrderReviewPriceListWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const ScrollableMiddle = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  margin-bottom: ${pxToRem(12)};
`;

const uLayoutStyle = css`
  height: 100%;
  overflow: hidden;
`;

const OrderReview = ({
  wizardData,
  setWizardData,
  isPostPaidVisible,
  setWizardTitle,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  isProductPage,
  handleNext,
  setGoBackHandler,
  handleBack,
  isSegregated,
}) => {
  const { t } = useTranslation();
  const {
    storageProduct,
    // selectedPaymentMethod,
    selectedVault,
    calculation,
    expiryTime,
    isVaultActiveTab,
    selectedAddress,
    updatedPaymentMethods,
    productTiers,
    quoteID,
    selectedPlan,
  } = wizardData;
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const { RootUrl } = useSelector(selectSettings);
  const imageSource = config.algoliaSearch.imageCode
    .replace('{product_code}', storageProduct?.ProductCode)
    .replace('{base_url}', RootUrl);
  const prevPaymentMethodRef = React.useRef(selectedPaymentMethod);
  const [isLoadingGetQuotes, setIsLoadingGetQuotes] = useState(false);
  const [isFundsModalOpen, setIsFundsModalOpen] = useState(false);
  const [counter, setCounter] = useState(COUNTDOWN_TIMER_SEC);
  const [unity, setUnity] = useState(false);
  const [expirationMs, setExpirationMs] = useState(0);
  const [quoteUid, setQuoteUid] = useState(quoteID || '');
  const [paymentMethodData, setPaymentMethodData] = useState(
    calculation?.paymentMethodStepData || {},
  );
  const {
    displayIRAEntrustOptionsPermission,
    displayIRAMilleniumOptionsPermission,
    displayIRAEquityOptionsPermission,
    displayStandardOptionsPermission,
  } = useCheckPermissions();
  const account = useSelector(getCurrentAccount);

  const isSegregatedProduct = !isProductPage
    ? selectedPlan === 'premium'
    : isSegregated;

  const paymentMethodInfo =
    selectedPaymentMethod?.PaymentMethodType === 3
      ? paymentMethodData?.DataWithCCFee
      : paymentMethodData?.DataWithoutCCFee;

  const paymentDiscountValue1 = calculation?.paymentTypeDiscount;
  const paymentDiscountValue2 =
    paymentMethodData?.DataWithCCFee?.TotalClientExecutionPrice -
    paymentMethodData?.DataWithoutCCFee?.TotalClientExecutionPrice;
  const paymentDiscountValue =
    paymentDiscountValue1 !== null
      ? paymentDiscountValue1
      : paymentDiscountValue2;

  const [paymentDiscount, setPaymentDiscount] = useState(
    paymentDiscountValue || 0,
  );

  const [openSelectPaymentOptions, setOpenSelectPaymentOptions] = useState(
    false,
  );
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [openPriceTiersDrawer, setOpenPriceTiersDrawer] = useState(false);
  const [isLoadingExecuteQuotes, setIsLoadingExecuteQuotes] = useState(false);
  const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
  const handleEventBuyFlowStep = useBuyFlowStepNameEvent();

  const quantityExecuted = isPostPaidVisible
    ? paymentMethodInfo?.QuantityExecuted
    : calculation?.quantity;

  useEffect(() => {
    const stableBackHandler = () => {
      handleBack();
    };

    setGoBackHandler(() => stableBackHandler);

    return () => {
      setGoBackHandler(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setGoBackHandler]);

  useEffect(() => {
    setWizardTitle(t('buyWizard.reviewBuy'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!expirationMs) {
      if (expiryTime) {
        handleExpiration(expiryTime, setExpirationMs);
      }
    }
  }, [expirationMs, expiryTime]);

  useEffect(() => {
    const prev = prevPaymentMethodRef.current;
    const curr = selectedPaymentMethod;

    const hasChanged =
      prev?.PaymentMethodUid !== curr?.PaymentMethodUid ||
      prev?.PaymentMethodType !== curr?.PaymentMethodType;

    if (hasChanged && curr) {
      fetchQuotes();
    }

    prevPaymentMethodRef.current = curr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod]);

  const totalPrice = useCallback(() => {
    if (isPostPaidVisible) {
      if (calculation?.salesTax > 0 || calculation?.deliveryFee > 0) {
        return (
          paymentMethodInfo?.TotalClientExecutionPrice +
          calculation?.salesTax +
          calculation?.deliveryFee
        );
      }
      return paymentMethodInfo?.TotalClientExecutionPrice;
    }
    if (calculation?.salesTax > 0 || calculation?.deliveryFee > 0) {
      return (
        calculation?.estimatedOrderTotal +
        calculation?.salesTax +
        calculation?.deliveryFee
      );
    }
    return calculation?.estimatedOrderTotal;
  }, [calculation, paymentMethodInfo, isPostPaidVisible]);

  const onHandleGetQuotesSuccess = (getQuoteResult) => {
    const {
      QuoteUid,
      ExpiresOnUtc,
      QuoteStatusItem: {
        QuantityExecuted,
        ClientPricePerUnit,
        TotalClientExecutionPrice,
        VolumeDiscount,
        PaymentTypeDiscount,
        PaymentMethodStepData,
        DeliveryFee,
        SalesTax,
      },
    } = getQuoteResult;

    const calculation = {
      estimatedOrderTotal: TotalClientExecutionPrice,
      newPricePerUnit: ClientPricePerUnit,
      volumeDiscount: VolumeDiscount,
      quantity: QuantityExecuted,
      paymentTypeDiscount: PaymentTypeDiscount,
      paymentMethodStepData: PaymentMethodStepData,
      deliveryFee: DeliveryFee,
      salesTax: SalesTax,
    };
    setQuoteUid(QuoteUid);
    // setPricePerUnit(ClientPricePerUnit);
    // setQuantity(QuantityExecuted);
    // setClientExecutionPrice(TotalClientExecutionPrice);
    // setVolumeDiscount(VolumeDiscount);
    handleExpiration(ExpiresOnUtc, setExpirationMs);
    setPaymentDiscount(PaymentTypeDiscount);
    setPaymentMethodData(PaymentMethodStepData);

    setWizardData({
      ...wizardData,
      calculation,
      quoteID: QuoteUid,
      expiryTime: ExpiresOnUtc,
    });
  };

  const counterUpdater = useCallback((epoch) => {
    if (epoch?.data) {
      const [counter, unity] = epoch.data;
      setCounter((prevCounter) => prevCounter - prevCounter + counter);
      setUnity(unity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQuotes = () => {
    const paymentTypeUid =
      selectedPaymentMethod?.PaymentMethodType !== 1
        ? selectedPaymentMethod?.PaymentMethodUid
        : ALL_ZEROES_UID;
    const payload = {
      getQuotesPayload: {
        accountUid,
        symbolCode: selectedVault?.SymbolCode,
        quantityOrAmount:
          wizardData?.orderValues?.CashAmount ||
          wizardData?.orderValues?.QuantityAmount,
        sideType: 'Buy',
        unitType: wizardData?.activeType === 'dollars' ? 'Cash' : 'Quantity',
        paymentType: paymentTypes[selectedPaymentMethod?.PaymentMethodType],
        addressUid: selectedAddress?.AddressUid,
      },
      isLoading: setIsLoadingGetQuotes,
      onSuccess: onHandleGetQuotesSuccess,
      // onError: onHandleGetQuotesError,
      isSegregated: isSegregatedProduct,
      paymentTypeUid,
      addPaymentMethodData: true,
      orderEntryType: ORDER_ENTRY_TYPES.VERIFICATION,
    };
    setIsLoadingGetQuotes(true);
    if (isVaultActiveTab) {
      dispatch(
        getQuotesForOrderStorageBuy({
          ...payload,
        }),
      );
    } else {
      dispatch(
        getQuotesForBuyDelivery({
          ...payload,
        }),
      );
    }
  };

  useEffect(() => {
    if (counter === 2 && selectedVault?.SymbolCode) {
      fetchQuotes();
      setCounter(COUNTDOWN_TIMER_SEC);
    }

    return () => dispatch(emptyQuotesOrderStorageBuy());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    counter,
    accountUid,
    dispatch,
    wizardData?.activeType,
    selectedVault?.SymbolCode,
  ]);

  useStartTimer(
    expirationMs,
    counterUpdater,
    isLoadingGetQuotes || isLoadingExecuteQuotes,
  );

  const handleOpenPriceTiers = useCallback(() => {
    setOpenPriceTiersDrawer(true);
  }, [setOpenPriceTiersDrawer]);

  function paymentMethodRowItem() {
    if (selectedPaymentMethod) {
      if (selectedPaymentMethod?.PaymentMethodType === 1) {
        return t('buyWizard.payment.paymentMethods.AccountBalance');
      }
      if (selectedPaymentMethod?.PaymentMethodType === 2) {
        return `${selectedPaymentMethod?.BankName}, ${'***'}${
          selectedPaymentMethod?.BankAccountMask
        }`;
      }
      if (selectedPaymentMethod?.PaymentMethodType === 3) {
        return `${t('buyWizard.payment.paymentMethods.CreditCard')} ${'***'}${
          selectedPaymentMethod?.Last4Digits
        }`;
      }
    } else {
      return t('buyWizard.payment.emptyPaymentRow');
    }
  }

  useEffect(() => {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const existingScript = document.getElementById('sig-api');
    if (!existingScript) {
      loadSignifydDeviceFingerprintScriptWithoutSessionId(sessionId);
    }
  }, []);

  const onConfirmOrder = () => {
    setWizardTitle(t('buyWizard.buyForStorage.title'));
    handleNext();
  };

  const onHandleExecuteQuotesSuccess = (getExecuteQuoteResult) => {
    const { TransactionUid } = getExecuteQuoteResult;

    setWizardData({
      ...wizardData,
      transactionUid: TransactionUid,
    });
    onConfirmOrder();
    // setCloseIconVisible(true);
    // verifyOrderEcommerceGtmHelper({
    //   ecommerceGtmEvent,
    //   wizardData,
    //   finalOrder,
    //   account,
    //   orderSideType: 'buy',
    //   quantity,
    //   isForDelivery: false,
    //   orderCode: quoteUid || quoteID,
    // });

    handleEventBuyFlowStep(
      account,
      BUY_FLOW_STEPS_ENUM.REVIEW_ORDER,
      wizardData?.storageProduct,
      BUY_FLOW_ORDER_TYPE.buyForStorage,
    );
  };

  const onHandleSuccess = () => {
    // verifyOrderEcommerceGtmHelper({
    //   ecommerceGtmEvent,
    //   wizardData,
    //   finalOrder,
    //   account,
    //   orderSideType: 'buy',
    //   quantity,
    //   isForDelivery: false,
    //   orderCode,
    // });

    if (isProductPage) {
      dispatch(
        fetchPortfolioProductBySymbol({
          accountUid,
          symbol: selectedVault.SymbolCode,
          isSegregated,
        }),
      );
      dispatch(
        accountTransactionsFetch({
          accountUid,
          productSymbol: selectedVault.SymbolCode,
          pageNumber: 0,
          pageSize: 20,
          content: PRODUCT_TRANSACTIONS,
        }),
      );
    }
  };

  const handleConfirmOrder = () => {
    const payload = {
      executeQuotePayload: {
        quoteUid: quoteUid || quoteID,
        signifydSessionId:
          isPostPaidVisible && selectedPaymentMethod?.PaymentMethodType !== 1
            ? sessionId
            : 'null',
      },
      isLoading: setIsLoadingExecuteQuotes,
      onSuccess: onHandleExecuteQuotesSuccess,
      onConfirm: onHandleSuccess,
      // onError: onHandleExecuteQuotesError,
    };
    // setCloseIconVisible(false);
    setIsLoadingExecuteQuotes(true);
    dispatch(
      executeQuotesForOrderStorageBuy({
        ...payload,
      }),
    );
  };

  return (
    <>
      <SectionLoader
        isLoading={isLoadingGetQuotes || isLoadingExecuteQuotes}
        css={uLayoutStyle}
      >
        <BasicModalBody as={ModalLayout}>
          <ProductInfoWrapper>
            <ProductImage src={encodeURI(imageSource)} alt="Product" />
            <ProductName>
              {t('buyWizard.buying')}
              <b>
                {quantityExecuted} x {storageProduct?.ShortCaption}
              </b>
            </ProductName>

            <PriceLockTimer
              counter={counter}
              unity={unity}
              loading={isLoadingGetQuotes}
            />
          </ProductInfoWrapper>
          <ScrollableMiddle>
            <OrderReviewPriceListWrapper>
              <OrderReviewPriceList
                paymentMethodInfo={paymentMethodInfo}
                paymentDiscount={paymentDiscount}
                calculation={calculation}
                onClick={handleOpenPriceTiers}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentDiscountValue2={paymentDiscountValue2}
                isPostPaidVisible={isPostPaidVisible}
                totalPrice={totalPrice}
              />
            </OrderReviewPriceListWrapper>
          </ScrollableMiddle>
          {isPostPaidVisible && (
            <SavingInfoWrapper
              isCCSelected={selectedPaymentMethod?.PaymentMethodType === 3}
            >
              {selectedPaymentMethod?.PaymentMethodType === 3 ? (
                <>
                  <InfoIcon />
                  <Paragraph>
                    {t('buyWizard.reviewStepSavingInfo1')}
                    <b>
                      {formatMoneyNumeral(
                        paymentDiscount ?? paymentDiscountValue2,
                      )}
                    </b>
                  </Paragraph>
                </>
              ) : (
                <>
                  <HandCoinsIcon />
                  <Paragraph isCCSelected>
                    {t('buyWizard.reviewStepSavingInfo')}
                    <b>
                      {formatMoneyNumeral(
                        paymentDiscount ?? paymentDiscountValue2,
                      )}{' '}
                      (4%)
                    </b>
                  </Paragraph>
                </>
              )}
            </SavingInfoWrapper>
          )}

          <RowItemElement
            label={t('buyWizard.payWith')}
            value={
              <>
                {
                  PAYMENT_METHOD_TYPES_ICONS[
                    selectedPaymentMethod?.PaymentMethodType
                  ]
                }
                {paymentMethodRowItem()}
              </>
            }
            onClick={() => {
              if (isPostPaidVisible) {
                setOpenSelectPaymentOptions(true);
              } else {
                setIsFundsModalOpen(true);
                // setWizardContent(IS_FUND_MODAL_OPEN, true);
              }
            }}
            clickLabel={!isPostPaidVisible && t('buyWizard.addFunds')}
          />
          <ButtonWrapper>
            <ModalButtons
              isVertical
              showOnlyPrimary
              primaryButtonProps={{
                onClick: () => {
                  handleConfirmOrder();
                },
                label: t('buyWizard.buyNow'),
                textTransform: 'capitalize',
                marginTop: '10',
                'data-cy': 'button-confirm',
              }}
            />
          </ButtonWrapper>
        </BasicModalBody>
      </SectionLoader>
      <SelectPaymentOptions
        open={openSelectPaymentOptions}
        setOpen={setOpenSelectPaymentOptions}
        wizardData={wizardData}
        setWizardData={setWizardData}
        paymentMethods={updatedPaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        setIsAddAddressOpen={setIsAddAddressOpen}
        isAddAddressOpen={isAddAddressOpen}
        accountUid={accountUid}
      />
      <PriceTiers
        open={openPriceTiersDrawer}
        setOpen={setOpenPriceTiersDrawer}
        productTiers={productTiers}
        storageProduct={storageProduct}
        currentSpend={totalPrice()}
      />
      {displayStandardOptionsPermission && isFundsModalOpen && (
        <StandardDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            // setWizardContent(IS_FUND_MODAL_OPEN, false);
            deleteWizardKey(STANDARD_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
          }}
          overModal
          avoidClearingStorage
        />
      )}

      {displayIRAEquityOptionsPermission && isFundsModalOpen && (
        <IraDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => setIsFundsModalOpen(false)}
          type={IRA_EQUITY_TYPE}
          overModal
          avoidClearingStorage
        />
      )}
      {displayIRAEntrustOptionsPermission && isFundsModalOpen && (
        <IraDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => setIsFundsModalOpen(false)}
          type={IRA_ENTRUST_TYPE}
          overModal
          avoidClearingStorage
        />
      )}
      {displayIRAMilleniumOptionsPermission && isFundsModalOpen && (
        <MilleniumDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => setIsFundsModalOpen(false)}
          overModal
          avoidClearingStorage
        />
      )}
    </>
  );
};

OrderReview.propTypes = {
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
      Locations: PropTypes.arrayOf(PropTypes.shape({})),
      ProductCode: PropTypes.string,
      ShortCaption: PropTypes.string,
      setWizardTitle: PropTypes.func,
    }),
    selectedPlan: PropTypes.string,
    activeType: PropTypes.string,
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      PricePerUnit: PropTypes.number,
      SymbolCode: PropTypes.string,
      HasDelayedSettlement: PropTypes.bool,
      BuyMinimumAmount: PropTypes.number,
    }),
    calculation: PropTypes.shape({
      estimatedOrderTotal: PropTypes.number,
      newPricePerUnit: PropTypes.number,
      volumeDiscount: PropTypes.number,
      quantity: PropTypes.number,
      paymentTypeDiscount: PropTypes.number,
      paymentMethodStepData: PropTypes.shape({}),
      salesTax: PropTypes.number,
      deliveryFee: PropTypes.number,
    }),
    orderValues: PropTypes.shape({
      QuantityAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      CashAmount: PropTypes.number,
    }),
    quoteID: PropTypes.string,
    expiryTime: PropTypes.string,
    isVaultActiveTab: PropTypes.bool,
    selectedAddress: PropTypes.shape({
      AddressUid: PropTypes.string,
    }),
    updatedPaymentMethods: PropTypes.arrayOf({}),
    productTiers: PropTypes.arrayOf({}),
  }),
  setWizardData: PropTypes.func,
  isPostPaidVisible: PropTypes.bool,
  setWizardTitle: PropTypes.func,
  selectedPaymentMethod: PropTypes.shape({
    PaymentMethodType: PropTypes.number,
    PaymentMethodUid: PropTypes.number,
    Last4Digits: PropTypes.number,
    BankName: PropTypes.string,
    BankAccountMask: PropTypes.number,
  }),
  setSelectedPaymentMethod: PropTypes.func,
  isProductPage: PropTypes.bool,
  handleNext: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  handleBack: PropTypes.func,
  isSegregated: PropTypes.bool,
};

export default OrderReview;
